/**
 * OneSignal push notification helpers for the native Capacitor app.
 *
 * All exported functions guard with Capacitor.isNativePlatform() and are safe
 * to call unconditionally — they are silent no-ops in a browser or on web.
 *
 * SDK: onesignal-cordova-plugin v5 (Capacitor-compatible).
 *
 * Flow:
 *   1. initPush()             — called once on app mount (useAuth.ts)
 *   2. requestPushPermission()— called on SIGNED_IN event (right after login)
 *   3. APNs/FCM registers → pushSubscription.change listener fires
 *   4. Listener POSTs subscription ID to /api/push/register automatically
 *   5. unregisterPush()       — called on SIGNED_OUT to clear DB record
 *
 * Concurrency model:
 *   initPush() returns a memoised promise so requestPushPermission() can
 *   await it knowing the SDK is fully loaded before any permission call.
 *
 * SDK lookup strategy (defensive, multi-path):
 *   Cordova plugins in a Capacitor build can expose themselves via two
 *   mechanisms, depending on plugin packaging:
 *     (a) ES module export — `await import('onesignal-cordova-plugin')`
 *         works because the npm package has a "main" entry pointing to
 *         a real JS module. This is the modern v5 pattern.
 *     (b) Runtime global — Cordova's old-school bridge attaches
 *         `window.cordova.plugins.OneSignal` (or similar) when the
 *         native side finishes loading. Older plugin versions only do
 *         this and have no usable ES export.
 *   getOneSignal() tries (a) first, falls back to (b) so we work
 *   regardless of which path the runtime actually exposes. If neither
 *   resolves we surface a console.warn — symptom on iOS would be "no
 *   permission prompt appears" because the SDK never loaded.
 *
 * Diagnostics:
 *   Each step logs to console.warn on failure (visible in iOS Safari Web
 *   Inspector via a Mac), and writes a status string to sessionStorage
 *   under 'push.status' so an in-app debug surface can read it without
 *   needing a Mac.
 */
import { Capacitor } from '@capacitor/core'
import { resolvePushNavigation } from './pushNavigation'
import { markPushNav } from './sessionResume'
import { stashPendingRoute } from './pendingRoute'

const APP_ID = import.meta.env.VITE_ONESIGNAL_APP_ID as string | undefined

// Memoised init promise. Subsequent calls return this same promise so
// callers can `await initPush()` knowing the SDK is ready.
let _initPromise: Promise<void> | null = null

// Write a short status string to sessionStorage so the user (or a debug
// panel) can read what happened with the push init without needing iOS
// console access via Mac. Keys: 'push.status', 'push.error'.
function logPushStatus(status: string, error?: unknown): void {
  try {
    sessionStorage.setItem('push.status', status)
    if (error !== undefined) {
      const msg = error instanceof Error ? `${error.name}: ${error.message}` : String(error)
      sessionStorage.setItem('push.error', msg)
    }
  } catch {
    // sessionStorage might not be available in some weird WebView states
  }
}

/**
 * Resolve the OneSignal SDK reference, trying multiple paths.
 *
 * Returns the OneSignal namespace object (with .initialize, .Notifications,
 * .User, etc.) or null if no path resolved. We try ES module import first,
 * then various runtime globals that the Cordova plugin might attach to.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function getOneSignal(): Promise<any | null> {
  // NOTE (2026-08-05): the old path (a) — a dynamic
  // `import(/* @vite-ignore */ 'onesignal-cordova-plugin')` — was dead code
  // on every platform, every launch: @vite-ignore emits the BARE specifier
  // verbatim into the bundle, and a browser/WebView import() has no
  // resolution for bare specifiers, so it threw
  // "Failed to resolve module specifier" and warned on each call (~3×/session
  // in Sentry). The runtime globals below are how the Cordova plugin actually
  // arrives (Capacitor injects cordova.js + the plugin, which clobbers
  // window.OneSignal and window.plugins.OneSignal). Path (a) deleted.

  // Runtime globals — Cordova attaches plugins to window
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any
  const candidates = [
    w?.cordova?.plugins?.OneSignal,
    w?.plugins?.OneSignal,
    w?.OneSignal,
  ]
  for (const c of candidates) {
    if (c && typeof c.initialize === 'function') {
      return c
    }
  }

  return null
}

async function doInitPush(): Promise<void> {
  if (!Capacitor.isNativePlatform() || !APP_ID) {
    logPushStatus('skipped: not native or no APP_ID')
    return
  }

  const OneSignal = await getOneSignal()
  if (!OneSignal) {
    logPushStatus('failed: OneSignal SDK not found via any path', new Error('SDK not available'))
    console.warn('[push] OneSignal SDK not available at runtime (window globals not yet injected)')
    // THROW rather than return: initPush() memoises this promise, and a
    // clean return cached a RESOLVED no-op init — so when this raced ahead
    // of cordova's DOMContentLoaded bootstrap (module scripts run first),
    // the click + subscription listeners silently never attached for the
    // whole session. Rejecting clears the memo, so the next call (e.g.
    // requestPushPermission on SIGNED_IN) retries with the globals present.
    throw new Error('OneSignal SDK not available yet')
  }

  try {
    OneSignal.initialize(APP_ID)
    logPushStatus('initialized')
  } catch (err) {
    logPushStatus('failed: OneSignal.initialize threw', err)
    console.warn('[push] OneSignal.initialize threw:', err)
    throw err
  }

  // ── Notification tap → navigate to the right tab ─────────────────────────
  // The backend includes additionalData: { tab: '<tab-name>' } on every push
  // (see backend/services/push_service.py). When the user taps a notification,
  // this listener fires (foreground, background, and cold-start). Two paths:
  //   • Already on /app (MainApp mounted) → dispatch trybeup:switch-tab and
  //     the in-app handler switches without a navigation. Smooth, no reload.
  //   • Anywhere else (/profile, /admin, /onboarding, etc.) → MainApp isn't
  //     mounted so there's no listener; we have to actually navigate. The
  //     URL-based ?tab= handler at MainApp mount-time picks it up.
  // (Founder report 2026-06-17: tapping a foreground notification banner
  // from the Profile screen did nothing — the event was being dispatched
  // into the void because Profile didn't have the listener attached.)
  try {
    OneSignal.Notifications.addEventListener('click', (event: { notification?: { additionalData?: Record<string, unknown> | null } | null }) => {
      // Tell the resume-reset (sessionResume) that a notification tap is
      // navigating right now, so "15 min away → My Day" doesn't override the
      // push's own destination (a duel, a Trybe, a habit reminder).
      markPushNav()
      // Guard the whole chain, not just additionalData — a malformed click event
      // with no `notification` would otherwise throw inside the listener (the
      // outer try/catch only guards listener REGISTRATION, not each invocation).
      const data = (event?.notification?.additionalData ?? {}) as Record<string, unknown>

      // A/B copy tracking: if the push was tagged with a beat (set by
      // the proactive Assistant schedulers — morning / afternoon /
      // evening / streak_risk), fire an assistant_push_opened analytics
      // event so the open-rate per variant can be aggregated. Fire-and-
      // forget; never block the navigation on an analytics insert.
      const beat = data.beat as string | undefined
      const variant = data.variant as string | undefined
      if (beat) {
        import('./analytics')
          .then(({ trackEvent }) =>
            trackEvent('assistant_push_opened', {
              properties: { beat, variant: variant ?? null },
            }),
          )
          .catch(() => { /* analytics down — push still navigates */ })
      }

      const tab = data.tab as string | undefined

      // CTA deep-link: the proactive Assistant pushes (morning /
      // afternoon / evening) include an `action` in additionalData
      // (start | close | pick) that tells MissionCard which modal to
      // open on landing. Pre-2026-06-19 this handler only forwarded
      // `tab` and silently dropped `action` — the founder reported
      // "morning / mid-day / evening prompts just take me to My Day"
      // because the picker / ritual never opened. Now we:
      //   (a) stash the action into sessionStorage immediately so the
      //       URL-parser path (MainApp on mount) and the foreground
      //       path (MissionCard already mounted) both consume the same
      //       value;
      //   (b) include action in the navigation URL for the cold-start
      //       path so MainApp's existing parser can also see it (belt
      //       + suspenders — either source works);
      //   (c) dispatch `trybeup:cta-action-stashed` so an
      //       already-mounted MissionCard re-runs its consume logic
      //       without waiting on its mount-effect deps to change.
      const rawAction = data.action as string | undefined
      const action = rawAction && ['start', 'close', 'pick'].includes(rawAction) ? rawAction : null
      if (action) {
        try { sessionStorage.setItem('trybeup_myday_action', action) } catch { /* ignore */ }
      }

      // No early-return on a missing tab any more: a tab-less push may still
      // carry a full-route `path` (a "new message" → /messages, a "new
      // follower" → /profile/{id}). resolvePushNavigation turns that into a
      // hard-nav; only a push with neither tab nor path resolves to 'none'
      // (a generic broadcast — nothing specific to open). Previously
      // `if (!tab) return` dropped every message/follower tap on My Day.

      // When authenticated, MainApp renders at BOTH '/' and '/app' (see the
      // AuthenticatedApp routes in App.tsx). So on a notification COLD-START
      // the app is already sitting on MainApp at '/'. The old code only
      // treated '/app' as "MainApp is here" and HARD-NAVIGATED
      // (window.location.assign) for everything else — including '/'. That
      // full reload re-runs the auth gate and briefly flashes the LOGIN
      // screen before the session re-resolves (founder report 2026-06-28:
      // "tapping the morning nudge shows the login screen; a restart logs me
      // straight back in"). The session was never lost — it's a reload race.
      //
      // Fix: soft-switch (no reload, no login flash) whenever MainApp is
      // already mounted ('/' or '/app') AND the tab is one the switch-tab
      // event fully handles. To survive the cold-start race where the click
      // fires while the auth gate is still resolving (MainApp not yet mounted,
      // so the dispatched switch-tab event has no listener), we ALSO stash the
      // tab in sessionStorage — MainApp reads it on mount, exactly the way
      // ?tab= used to survive the reload.
      //
      // challenges/trybe MUST take the hard-nav path: they carry an entity
      // (duelId / trybeId / eventTab) that only MainApp's ?tab= URL parser
      // restores — the switch-tab event only carries the bare tab string, so
      // a soft-switch lands the user on the generic tab with no duel/trybe
      // selected. The soft path is therefore gated on SOFT_SWITCH_TABS in
      // EVERY case (the old `onAppRoute ||` short-circuit wrongly soft-switched
      // challenges/trybe whenever the app was already on /app — warm-start taps
      // from a duel/trybe push dropped the entity; 2026-06-28 review).
      const nav = resolvePushNavigation({
        tab,
        action,
        data,
        pathname: window.location.pathname,
      })
      if (nav.kind === 'soft') {
        // Soft path — no reload. Stash the tab so a not-yet-mounted MainApp
        // still picks it up on mount (cleared there and in the switch-tab
        // handler so it can't be re-applied stale on a later remount).
        try { sessionStorage.setItem('trybeup_pending_tab', nav.tab) } catch { /* ignore */ }
        window.dispatchEvent(new CustomEvent('trybeup:switch-tab', { detail: nav.tab }))
        if (nav.fireAction) {
          window.dispatchEvent(new CustomEvent('trybeup:cta-action-stashed'))
        }
      } else if (nav.kind === 'restore') {
        // Restore path — an entity-carrying tab (duel / trybe / challenge
        // program) while MainApp is (or is about to be) mounted. Apply the
        // deep-link IN PLACE, NO reload, so a cold-start tap never flashes the
        // login screen the way a full navigation did (founder report: a duel
        // push briefly showed the sign-in page). Two channels, same as the soft
        // path: stash the full query for the cold-start case where this fires
        // before MainApp mounts its listener, AND dispatch the event for the
        // warm case where MainApp is already listening. MainApp clears the stash
        // once it consumes it so it can't be re-applied stale.
        const qIndex = nav.url.indexOf('?')
        const query = qIndex >= 0 ? nav.url.slice(qIndex + 1) : ''
        try { sessionStorage.setItem('trybeup_pending_deeplink', query) } catch { /* ignore */ }
        window.dispatchEvent(new CustomEvent('trybeup:apply-deeplink', { detail: query }))
      } else if (nav.kind === 'hard') {
        // Off-MainApp routes (MainApp not mounted), admin-surface tabs, AND
        // full-route destinations from a tab-less push (a message →
        // /messages, a follower → /profile/{id}). The resolved URL forwards
        // every deep-link param so the destination's mount-time parser can
        // restore the entity.
        //
        // This used to be `window.location.assign(nav.url)` — a full WebView
        // reload, which re-runs the cold-start auth gate. That is the same
        // race the soft/restore paths above were built to avoid, and it bit
        // the Concierge push: tapping "Concierge · Markus" reloaded into a
        // gate that hadn't resolved a session yet, and the unauthenticated
        // catch-all redirected to /login — landing the founder on the SIGN-IN
        // screen with the destination erased (report 2026-08-12).
        //
        // Navigate inside the SPA instead: no reload, so the session is never
        // re-resolved and there is no race to lose. Two channels, mirroring
        // the restore path — dispatch for a mounted app, and a durable stash
        // for the cold-start case where this fires before the router exists.
        // PendingRouteHandler (App.tsx) consumes whichever arrives.
        stashPendingRoute(nav.url)
        window.dispatchEvent(new CustomEvent('trybeup:navigate', { detail: nav.url }))
      }
      // nav.kind === 'none' → a tab-less, path-less push (generic broadcast):
      // nothing specific to open, so leave the user wherever the app is.
    })
  } catch (err) {
    console.warn('[push] failed to attach click listener:', err)
  }

  // ── Auto-register when the subscription ID arrives / changes ─────────────
  // Fires when requestPushPermission() succeeds and APNs/FCM registers the
  // device, and again if the subscription ID rotates (e.g. after reinstall).
  // We POST the new ID to the backend so the DB stays current. Best-effort.
  try {
    OneSignal.User.pushSubscription.addEventListener('change', async (sub: { current?: { id?: string | null } | null }) => {
      const subId = sub.current?.id
      if (!subId) return
      try {
        const { apiFetch } = await import('@/lib/api')
        const res = await apiFetch('/api/push/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subscription_id: subId }),
        })
        if (!res.ok) {
          console.warn('[push] register failed:', res.status)
        }
      } catch (err) {
        console.warn('[push] register threw:', err)
      }
    })
  } catch (err) {
    console.warn('[push] failed to attach subscription change listener:', err)
  }
}

/**
 * Initialize the OneSignal SDK. Idempotent — repeat calls return the same
 * promise. Must be called once on app mount; safe to call again later.
 */
export function initPush(): Promise<void> {
  if (!_initPromise) {
    _initPromise = doInitPush().catch((err) => {
      // Don't let a failed init poison future calls — clear the cached
      // promise so a retry has a chance to succeed.
      console.warn('[push] initPush failed, will retry on next call:', err)
      _initPromise = null
      throw err
    })
  }
  return _initPromise
}

/**
 * Request OS push notification permission from the user.
 *
 * Call this right after login — NEVER on cold start (kills opt-in rates).
 * Awaits initPush() so the SDK is guaranteed ready before requesting.
 *
 * `fallbackToSettings: false` is intentional. When set to true, OneSignal
 * shows a "You currently have notifications turned off — Open Settings?"
 * dialog every time we call requestPermission against a previously-denied
 * permission state. Since useAuth fires this on every SIGNED_IN event,
 * denied users were getting that dialog every login. Respect the no.
 * Users who change their mind can re-enable from NotificationOptIn or
 * via iOS Settings on their own.
 */
export async function requestPushPermission(): Promise<void> {
  if (!Capacitor.isNativePlatform() || !APP_ID) {
    logPushStatus('permission: skipped (not native or no APP_ID)')
    return
  }
  try {
    await initPush()
    const OneSignal = await getOneSignal()
    if (!OneSignal) {
      logPushStatus('permission: failed (SDK not available)')
      console.warn('[push] requestPushPermission: SDK not available')
      return
    }
    // requestPermission returns the actual grant result — record granted vs
    // denied (not just "requested") so the diagnostics surface the real
    // opt-in/denial rate. Matters during a marketing push: a wave of denials
    // is invisible if every call logs an identical "requested". On grant, the
    // pushSubscription 'change' listener (attached in initPush) POSTs the new
    // id; on denial no APNs token is issued so there's intentionally nothing
    // more to do here. 2026-06-28 review.
    const granted = await OneSignal.Notifications.requestPermission(false)
    logPushStatus(`permission: ${granted ? 'granted' : 'denied'}`)
  } catch (err) {
    logPushStatus('permission: error', err)
    console.warn('[push] requestPermission error:', err)
  }
}

/**
 * Proactively reconcile the backend's stored OneSignal subscription id with
 * the device's CURRENT one, on every launch.
 *
 * Why this exists (founder report 2026-06-27: "I never get the automatic
 * Assistant nudges, but the test button says it sent"): the only path that
 * saved the subscription id was the `pushSubscription.change` listener in
 * doInitPush(), which fires on first-grant / id rotation. On a normal
 * relaunch of an already-subscribed device, `change` does NOT fire — so if
 * the backend ever held a STALE id (the device's id rotated while the app
 * was closed, e.g. after an app update/reinstall) or MISSING id (the initial
 * change event lost a race), nothing re-synced it. The scheduler then sent
 * every nudge to a dead address; OneSignal accepts the request (so the test
 * button reports "sent") but never delivers to the phone — silently, for all
 * four beats at once.
 *
 * This reads the live id directly (v5 getter) and POSTs it, self-healing the
 * stored id for everyone on their next launch. Retries briefly because the
 * native SDK can take a moment after launch to rehydrate the cached id.
 * Best-effort; never throws.
 */
export async function syncPushSubscription(): Promise<void> {
  if (!Capacitor.isNativePlatform() || !APP_ID) return
  try {
    await initPush()
    const OneSignal = await getOneSignal()
    if (!OneSignal) return

    let subId: string | null | undefined
    for (let i = 0; i < 6; i++) {
      subId = OneSignal.User?.pushSubscription?.id
      if (subId) break
      await new Promise((r) => setTimeout(r, 500))  // rehydration grace
    }
    if (!subId) {
      logPushStatus('sync: no live subscription id (device not subscribed?)')
      return
    }

    const { apiFetch } = await import('@/lib/api')
    const res = await apiFetch('/api/push/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subscription_id: subId }),
    })
    if (res.ok) {
      logPushStatus(`sync: reconciled subscription ${subId.slice(0, 8)}…`)
    } else {
      logPushStatus(`sync: register returned ${res.status}`)
    }
  } catch (err) {
    logPushStatus('sync: threw', err)
    console.warn('[push] syncPushSubscription threw:', err)
  }
}

/**
 * Unregister push notifications on sign-out.
 *
 * Clears onesignal_subscription_id and sets push_enabled = false in the DB.
 * Best-effort — errors are swallowed so sign-out is never blocked.
 */
export async function unregisterPush(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  try {
    const { apiFetch } = await import('@/lib/api')
    await apiFetch('/api/push/register', { method: 'DELETE' })
  } catch (err) {
    console.warn('[push] unregister threw:', err)
  }
}
